import { useCallback, useEffect, useState } from 'react';
import { ConfigProvider, message, notification } from 'antd';
import type {
  OpenTrade,
  OpenTradeComplete,
  IErrorProps,
  IUser,
  IControlData,
  IGameProgress,
  ISetupData,
  ITeamRoundData,
  IAllTeamAssets,
  IAsset,
  IComment,
  ILastTradedPrice
} from '@powertrader/schema';
import {
  TeamSwitch,
  setErrorContext,
  setupDataContext,
  teamAssetsContext,
  teamRoundDataContext,
  allTeamAssetsContext,
  gameProgressContext,
  controlDataContext,
  openTradesContext,
  bilateralTradesContext,
  openTradesCompletedContext,
  Chat,
  lastTradedPricesContext,
  socket,
  useGameFlowSocket
} from '@powertrader/core';

import './style.css';

import GamePlayArea from './Components/GamePlayArea/GamePlayArea';
import ControlArea from './Components/ControlArea/ControlArea';
import { LoginArea } from './Components/LoginArea/LoginArea';
import { useBilateralTradeProvider } from './store/bilateralTrades.store';
import { isBrowser } from 'react-device-detect';
import { MobileView } from './Components/Mobile';
import { BilateralQRDeleter } from './Components/Mobile/BilateralTrading/BilateralQRDeleter';

function App() {
  const [user, setUser] = useState<IUser>();
  const [error, setError] = useState<IErrorProps>(); // {message, error}
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [setupData, setSetupData] = useState<ISetupData>(); // Things that are always fixed
  const [controlData, setControlData] = useState<IControlData>(); // Things are cotrolled like the weather!
  const [teamRoundData, setTeamRoundData] = useState<ITeamRoundData[]>(); // TeamRound Scores and Data
  const [allComments, setAllComments] = useState<IComment[]>([]);
  const [controlPannel, setControlPannel] = useState<boolean | null>(null); // was set to null before
  const [teamAssets, setTeamAssets] = useState<IAsset[]>([]);
  const [allTeamAssets, setAllTeamAssets] = useState<IAllTeamAssets[]>([]);
  const [gameProgress, setGameProgress] = useState<IGameProgress>();
  const [openTrades, setOpenTrades] = useState<OpenTrade[]>([]);
  const [openTradesCompleted, setOpenTradesCompleted] = useState<OpenTradeComplete[]>([]);
  const [lastTradedPrices, setLastTradedPrices] = useState<ILastTradedPrice[]>([]);

  const bilateralTrades = useBilateralTradeProvider({ socket, currency: setupData?.settings.currency, user });

  const logout = useCallback(() => {
    socket.emit('leaveRoom', user);
    sessionStorage.clear();
    window.location.reload();
    message.info('Logging Out');
  }, [user]);

  useEffect(() => {
    if (error) {
      if (user?.teamType === 'ADMIN') {
        message.error(error.message);
      } else {
        message.error('An error has occurred and has been logged, if this error persists, please refresh your browser');
      }
      socket.emit('errorLog', { ...error, userID: user?.userID });
    }
  }, [error, user?.teamType, user?.userID]);

  useEffect(() => {
    if (isLoggedIn && user?.userID && user?.teamID && user?.teamType) {
      socket.emit('getAllSetupData', { userID: user.userID, teamID: user.teamID, teamType: user.teamType }, (err: IErrorProps['error']) => {
        setError({ message: 'cannot get SetupData', error: err });
      });
    }
  }, [isLoggedIn, user?.teamID, user?.teamType, user?.userID]);

  useEffect(() => {
    if (setupData?.settings) {
      setGameProgress({
        roundID: setupData.settings.currentRound,
        scenarioID: setupData?.settings.currentScenario
      });
    }
  }, [setupData?.settings]);

  useEffect(() => {
    const loggedInUser = sessionStorage.getItem('user');

    if (loggedInUser) {
      const foundUser = JSON.parse(loggedInUser);
      setUser(foundUser);
      setIsLoggedIn(true);
      setRequiresLogin(false);

      if (foundUser.teamType === 'ADMIN') {
        setControlPannel(true);
      } else {
        setControlPannel(false);
      }
    } else {
      setRequiresLogin(true);
    }
  }, [setIsLoggedIn, setUser]);

  useGameFlowSocket({ isLoggedIn, user, setControlData });
  useEffect(() => {
    if (isLoggedIn && user) {
      socket.on('connectionMade', () => {
        socket.emit('room', user);
      });

      socket.on('logoutUser', () => {
        setIsLoggedIn(false);
        logout();
      });
      socket.on('demoRoundEnded', () => {
        socket.emit('getAllSetupData', { userID: user.userID, teamID: user.teamID, teamType: user.teamType }, (err: IErrorProps['error']) => {
          setError({ message: 'cannot get SetupData', error: err });
        });
      });
      socket.on('controlData', (data: IControlData) => {
        if (data !== undefined) {
          setControlData(prevState => ({ ...prevState, ...data }));
        }
      });
      socket.on('setupData', (data: ISetupData) => {
        if (data !== undefined && user) {
          const userColor = data.teams.find(team => team.teamID === user.teamID)?.color || 'black';

          setUser(() => ({
            ...user,
            color: userColor
          }));

          setSetupData({ ...data, user: { ...user, color: userColor } });
        }
      });
      socket.on('updateSetupData', (data: Partial<ISetupData>) => {
        if (data !== undefined) {
          setSetupData(prevState => prevState && { ...prevState, ...data });
        }
      });

      interface IGameData {
        teamRoundData: ITeamRoundData[];
        teamAssets: IAsset[];
        allTeamAssets: IAllTeamAssets[];
        teamID: number;
      }
      socket.on('gameData', (data: IGameData, from: number) => {
        if (data !== undefined && (user.teamType === 'ADMIN' || user.teamID === data.teamID)) {
          if (data.teamRoundData) {
            setTeamRoundData(data.teamRoundData);
          }
          if (data.teamAssets && user.teamType !== 'ADMIN') setTeamAssets(data.teamAssets);

          if (user.teamType === 'ADMIN' && (data.allTeamAssets || data.teamAssets)) {
            setAllTeamAssets(prevState => {
              let newState: IAllTeamAssets[] = prevState ? [...prevState] : [];
              const index = newState.findIndex(teamData => teamData.teamID === data.teamID);

              if ((newState.length === 0 || Array.isArray(data.allTeamAssets)) && data.allTeamAssets) {
                return data.allTeamAssets;
              }
              if (index === -1) {
                // if not found

                const teamType = setupData?.teams.find(team => team.teamID === data.teamID)?.type || 'PLAYER';
                if (teamType) {
                  newState.push({
                    ...data,
                    teamType
                  });
                } else {
                  setError({
                    message: `Received gamesData but couldn't get teamType for allTeamAsset Data`
                  });
                }
              } else if (prevState) {
                newState = prevState.map(teamData => (teamData.teamID === data.teamID ? { ...teamData, teamAssets: data.teamAssets } : teamData));
              } else {
                setError({
                  message: `Prev State is undefined in AllTeamAsset`
                });
              }

              return newState;
            });
          }
        } else if (user.teamType === 'ADMIN' || user.teamID === data.teamID) {
          setError({
            message: `Received gamesData but couldn't validate teamID ${user.teamID} and dataTeamID ${data.teamID}`
          });
        } else {
          setError({
            message: `gameData is undefined from ln ${from || 'unknown'}`
          });
        }
      });

      socket.on('updateSetupAssets', (data: IAsset[]) => {
        if (data !== undefined) {
          setSetupData(prevState => {
            if (prevState) return { ...prevState, assets: data };
          });
          // Original Type Assets
        }
      });

      socket.on('allComments', (data: IComment[]) => {
        if (data !== undefined) {
          // console.log('coments', data)
          setAllComments(data);
        }
      });

      socket.on('updateComments', (dataNewComment: IComment) => {
        if (dataNewComment !== undefined) {
          setAllComments(prevState => [...prevState, dataNewComment]);
        }
      });

      // OPEN TRADE
      socket.on('openTrades', (openTradeData: OpenTrade[], openTradesCompletedData: OpenTradeComplete[]) => {
        setOpenTrades(openTradeData);
        setOpenTradesCompleted(openTradesCompletedData);

        const lastTradedPricesTemp = setupData?.markets.map(market => {
          const filteredTrades = openTradesCompletedData.filter(st => st.openTrade.market.marketID === market.marketID);
          return {
            market,
            price: filteredTrades[filteredTrades.length - 1]?.openTrade.price
          };
        });

        if (lastTradedPricesTemp) setLastTradedPrices(lastTradedPricesTemp);
      });

      socket.on('openTradeAdded', (newTrade: OpenTrade) => {
        if (newTrade)
          setOpenTrades(prevState => {
            const newState = [...prevState];
            newState.push(newTrade);
            return newState;
          });
      });

      socket.on('openTradeModified', (updatedTrade: OpenTrade) => {
        if (updatedTrade)
          setOpenTrades(prevState => {
            const newState = [...prevState];
            const tradeToModify = newState.findIndex(trade => trade.id === updatedTrade.id);
            if (tradeToModify !== -1) newState[tradeToModify] = updatedTrade;
            return newState;
          });
      });
      socket.on('openTradeAccepted', (parentTrade: OpenTrade, completedTrade: OpenTradeComplete, autoTrade?: boolean) => {
        if (!parentTrade || !completedTrade) throw new Error('Parent Trade or Completed Trade undefined');
        if (parentTrade.offeringTeam.teamID === user.teamID || (completedTrade.receivingTeam.teamID === user.teamID && autoTrade)) {
          message.success(
            `${completedTrade.volume}${parentTrade.market.volumeUnits} of your ${
              completedTrade.openTrade.dealType === 'sell' ? 'offer to sell' : 'bid to buy'
            } at ${completedTrade.openTrade.price}${parentTrade.market.priceUnits} was accepted`,
            5
          );
        }
        setOpenTrades(prevState => {
          const newState = [...prevState];
          const tradeIndex = newState.findIndex(trade => trade.id === parentTrade.id);
          if (tradeIndex !== -1) newState[tradeIndex] = parentTrade;

          return newState;
        });

        if (user.teamType === 'ADMIN' || parentTrade.offeringTeam.teamID === user.teamID || completedTrade.receivingTeam.teamID === user.teamID)
          setOpenTradesCompleted(prevState => {
            const newState = [...prevState];
            newState.push(completedTrade);
            return newState;
          });

        setLastTradedPrices(prevState => {
          const newState = [...prevState];
          const index = newState.findIndex(ns => ns.market.marketID === completedTrade.openTrade.market.marketID);
          if (index !== -1) {
            newState[index].price = completedTrade.openTrade.price;
          } else {
            newState.push({
              market: completedTrade.openTrade.market,
              price: completedTrade.openTrade.price
            });
          }
          return newState;
        });
      });

      socket.on('resetTrading', () => {
        setOpenTrades([]);
        setOpenTradesCompleted([]);
      });

      // NOTIFICATION SOCKETS
      socket.on('notification', (data: string) => {
        notification.open({
          message: 'Heuristic Team Notification',
          description: data,
          duration: 0,
          style: { borderBottom: `10px solid orange` }
        });
      });
    }

    socket.on('refresh', ({ optional, reason }: { optional?: boolean; reason?: string }) => {
      if (optional) {
        message.info('Please refresh: ' + reason);
      } else {
        window.location.reload();
      }
    });
    return () => {
      socket.off('connect');
      socket.off('updateSetupAssets');
      socket.off('controlData');
      socket.off('gameData');
      socket.off('setupData');
      socket.off('updateSetupData');
      socket.off('allComments');
      socket.off('demoRoundEnded');
      socket.off('updateComments');
      socket.off('openTrades');

      socket.off('openTradeAdded');
      socket.off('openTradeModified');
      socket.off('openTradeAccepted');

      socket.off('nextRound');
      socket.off('logoutUser');
      socket.off('marketData');
      socket.off('notification');
      socket.off('successNotification');
      socket.off('refresh');
    };
  }, [isLoggedIn, logout, setupData?.teams, user, setupData?.markets]);

  if (
    !isLoggedIn ||
    !(
      setupData &&
      controlData &&
      ((!controlPannel && teamAssets) || (controlPannel && allTeamAssets.length)) &&
      gameProgress &&
      teamRoundData &&
      controlPannel !== null &&
      user
    )
  ) {
    return (
      <>
        <setErrorContext.Provider value={setError}>
          {' '}
          <LoginArea
            setUser={setUser}
            setIsLoggedIn={setIsLoggedIn}
            setControlPannel={setControlPannel}
            requiresLogin={requiresLogin}
            isDemoSite={window.location.origin === 'https://workshopdemo.powertrader.online'}
          />
        </setErrorContext.Provider>
      </>
    );
  }
  if (window.location.pathname === '/chat') {
    return (
      <setupDataContext.Provider value={setupData}>
        <Chat popout user={user} allComments={allComments} setAllComments={setAllComments} logout={logout} />
      </setupDataContext.Provider>
    );
  }
  message.config({
    duration: user.role === 'ADMIN' ? 1 : undefined,
    maxCount: 2
  });

  return (
    <ConfigProvider>
      <setErrorContext.Provider value={setError}>
        <setupDataContext.Provider value={setupData}>
          <bilateralTradesContext.Provider value={bilateralTrades}>
            <teamRoundDataContext.Provider value={teamRoundData}>
              <gameProgressContext.Provider value={gameProgress}>
                <controlDataContext.Provider value={controlData}>
                  <openTradesContext.Provider value={openTrades}>
                    <openTradesCompletedContext.Provider value={openTradesCompleted}>
                      <lastTradedPricesContext.Provider value={lastTradedPrices}>
                        {controlPannel && (
                          <allTeamAssetsContext.Provider value={allTeamAssets}>
                            {isBrowser ? <ControlArea /> : <BilateralQRDeleter />}
                          </allTeamAssetsContext.Provider>
                        )}
                        {!controlPannel && (
                          <teamAssetsContext.Provider value={teamAssets}>
                            {isBrowser ? <GamePlayArea setTeamAssets={setTeamAssets} /> : <MobileView />}
                          </teamAssetsContext.Provider>
                        )}

                        {isBrowser && (
                          <Chat
                            user={user}
                            allComments={allComments}
                            setAllComments={setAllComments}
                            logout={logout}
                            forceShowChat={setupData.settings.currentGamePhase === 'portfolio' && !controlPannel}
                          />
                        )}
                        {user.role === 'ADMIN' && <TeamSwitch user={user} teams={setupData.teams} />}
                      </lastTradedPricesContext.Provider>
                    </openTradesCompletedContext.Provider>
                  </openTradesContext.Provider>
                </controlDataContext.Provider>
              </gameProgressContext.Provider>
            </teamRoundDataContext.Provider>
          </bilateralTradesContext.Provider>
        </setupDataContext.Provider>
      </setErrorContext.Provider>
    </ConfigProvider>
  );
}
export default App;
